// Sprint compaction-visibility (D11) — the founder-visible marker for a
// REAL CLI compaction: a horizontal divider with a compact-token label and
// a click-to-expand popover holding the summary the agent kept.
//
// Horizontal-reuse audit (universal-principles.md): no divider-with-label
// component existed when this shipped — `SystemPill` is a centred pill by
// contract, `ui/separator.tsx` is a bare rule — so it was composed from
// `Separator` + `Popover` (pattern: `ModelEffortChips.tsx:133-227`) +
// `MarkdownRenderer` + `max-h-96 overflow-y-auto` (precedent
// `AgentContextMenu.tsx:262`), zero new design tokens. Sprint
// machine-message-collapse (D7, 2026-09-12): the moment a second consumer
// appeared (`organisms/CollapsedMachineMessage.tsx`) the row itself moved
// to `DividerPopover.tsx`; this file keeps only what is compaction-specific
// (label math, preamble stripping, meta line, summary body) and its
// test-ids/pixels are unchanged by construction (`testIdPrefix`).
//
// CLICK, not hover (D11, R1 change 7): the repo's `hover-card.tsx` content
// IS hoverable/scrollable, so hover was technically available — the real
// reasons for click are a scroll surface of up to ~29k chars, accidental
// dismissal, keyboard/focus reachability, and Cursor's own
// summarize-divider being click-to-expand (`cursor-ux-mirror.md` §1
// binds). The trigger is a real `<button>` with `aria-expanded`; `title`
// carries the one-line hover hint.
//
// The popover BODY is the CLI's summary VERBATIM, English, un-translated —
// a DECLARED EXCEPTION to `founder-alignment.md` §Communication (R1 change
// 7): literal evidence beats a fabricated translation. Do not "fix" this
// with a model call.
//
// Sprint compaction-divider-v2 (D2, D3) — two defects fixed on top of the
// above: (1) the divider rendered ONE `Separator` before the label only
// (`———— [label]`, right-hugging) — a second, symmetric `Separator` after
// the Popover centers it (`—— [label] ——`), no token changes (F2); (2) a
// missing summary made the trigger `disabled` + `opacity-70` — a silent,
// dead control (`cursor-ux-mirror.md` §2.3 violation, F4 amended). The
// trigger is now ALWAYS enabled and always opens; the popover body renders
// the summary when present or `compaction.noSummary` when not — the FACT
// of the compaction (and its pre/post/duration meta) always communicates,
// it just no longer pretends the row is inert when it has less to show.

import { useState } from 'react';
import type { Message, SystemCompactionContent } from '../types';
import { useTranslation } from '../i18n';
import type { TKey } from '../i18n';
import { MarkdownRenderer } from './MarkdownRenderer';
import { DividerPopover } from './DividerPopover';

export interface CompactionDividerProps {
  message: Message;
}

// D11 (R1 change 6) — strip the CLI's continuation preamble through the
// first `Summary:` line ONLY when the text starts with the known prefix
// AND a `Summary:` line exists. Every other case renders verbatim. Never
// throws, never blanks: 2/119 real corpus summaries carry the prefix but
// NO `Summary:` line, and a `split('Summary:')[1]`-style implementation
// (forbidden by the spec) would blank those to an empty popover.
export function stripCompactionPreamble(summary: string): string {
  if (!summary.startsWith('This session is being continued')) return summary;
  const marker = 'Summary:';
  const idx = summary.indexOf(marker);
  if (idx === -1) return summary;
  const stripped = summary.slice(idx + marker.length).trimStart();
  return stripped.length > 0 ? stripped : summary;
}

// AC4 "label math (142k)". One decimal below 10k so small compactions
// (~1-2k freed) don't all collapse onto the same integer; whole numbers
// above 10k, which is where the real corpus (median freed ~8.5k, max in
// the tens of thousands) actually lives.
export function formatCompactTokens(n: number): string {
  if (!Number.isFinite(n) || n < 1000) return String(Math.max(0, Math.round(n)));
  const k = n / 1000;
  const rounded = k < 10 ? Math.round(k * 10) / 10 : Math.round(k);
  return `${rounded}k`;
}

function asCompaction(message: Message): SystemCompactionContent | null {
  const c = message.content;
  if (
    typeof c === 'object' &&
    c !== null &&
    (c as { subtype?: unknown }).subtype === 'compaction'
  ) {
    return c as SystemCompactionContent;
  }
  return null;
}

function metaKeyFor(trigger: string): TKey {
  if (trigger === 'auto') return 'compaction.popoverMetaAuto';
  if (trigger === 'manual') return 'compaction.popoverMetaManual';
  // D2 — trigger is an opaque string; anything else degrades silently to
  // the trigger-less variant instead of leaking the raw value.
  return 'compaction.popoverMeta';
}

export function CompactionDivider({ message }: CompactionDividerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const content = asCompaction(message);
  if (!content) return null;

  // A missing summary must never remove the fact a compaction happened
  // (D11). Sprint compaction-divider-v2 (D3, F4 amended): the trigger no
  // longer goes inert on a missing summary — it always opens, and the
  // popover body communicates via `compaction.noSummary` instead.
  const hasSummary = typeof content.summary === 'string' && content.summary.length > 0;
  const freedLabel = formatCompactTokens(content.freed_tokens);
  const dividerLabel = t('compaction.dividerLabel', { freed: freedLabel });
  const durationLabel = `${(content.duration_ms / 1000).toFixed(1)}s`;
  const metaLine = t(metaKeyFor(content.trigger), {
    pre: content.pre_tokens.toLocaleString(),
    post: content.post_tokens.toLocaleString(),
    duration: durationLabel,
  });

  return (
    <DividerPopover
      icon="Archive"
      label={dividerLabel}
      title={hasSummary ? dividerLabel : t('compaction.noSummary')}
      open={open}
      onOpenChange={setOpen}
      testIdPrefix="compaction-divider"
      rootAttrs={{ 'data-has-summary': hasSummary }}
    >
      <div className="flex flex-col gap-2">
        <p className="text-caption font-medium text-foreground">
          {t('compaction.popoverTitle')}
        </p>
        <p className="text-caption text-muted-foreground">{metaLine}</p>
        {hasSummary ? (
          <div className="max-h-96 overflow-y-auto" data-testid="compaction-summary-scroll">
            <MarkdownRenderer text={stripCompactionPreamble(content.summary as string)} />
          </div>
        ) : (
          <p className="text-caption text-muted-foreground">{t('compaction.noSummary')}</p>
        )}
      </div>
    </DividerPopover>
  );
}
